import { Injectable, inject } from '@angular/core';

import { CropFieldService } from '@next/arable-lib/crop-field';
import { CoordinateSystem } from '@next/arable-lib/geo';
import { CropFieldType } from '@next/arable-lib/shared';
import { CoreActions } from '@next/core-lib';
import { DIALOG_RETURN_TYPES, DialogService } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { SiteState } from '@next/core-lib/site';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store, select } from '@ngrx/store';
import { ProjectionLike, transform } from 'ol/proj';
import {
  EMPTY,
  Observable,
  finalize,
  from,
  map,
  mergeMap,
  of,
  startWith,
  switchMap,
  takeLast,
  withLatestFrom,
} from 'rxjs';
import { ExternalSource } from 'src/app/geo/models';
import {
  UpsertCropFieldSuccess,
  UpsertImportedCropFieldsRequest,
  UpsertImportedCropFieldsSuccess,
} from 'src/app/registration/store/actions/crop-field.actions';
import { getCropFields } from 'src/app/registration/store/selectors/crop-field.selectors';
import { getSoilTypes } from 'src/app/registration/store/selectors/soil-type.selectors';
import { isNutrientsPollutedArea } from 'src/app/shared/helpers/nutrient-polluted.helper';
import { CropField, SoilType } from 'src/app/shared/models';
import { CropFieldService as LocalCropFieldService } from 'src/app/shared/services/crop-field.service';
import { getCropFieldType, getCropYear } from 'src/app/store/selectors';

import { NvgActionTypes, UpdateNvgValues } from './actions';

@Injectable()
export class NvgEffects {
  protected readonly dialog = inject(DialogService);
  protected readonly store = inject<Store<SiteState>>(Store);
  protected readonly actions$ = inject(Actions);
  protected readonly locale = inject(LocaleService);
  protected readonly fieldService = inject(CropFieldService);
  private readonly localCropFieldService = inject(LocalCropFieldService);

  openUpdateNvgConfirmationDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NvgActionTypes.OpenNvgConfirmationDialog),
      switchMap((_) =>
        this.dialog
          .confirm({
            title: this.locale.translate('nvg-update-button-title'),
            text: this.locale.translate('nvg-confirmation-dialog-description'),
          })
          .pipe(
            switchMap(
              (result) =>
                (result.status === DIALOG_RETURN_TYPES.confirm && [new UpdateNvgValues()]) || EMPTY,
            ),
          ),
      ),
    ),
  );

  updateNvgValues$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(NvgActionTypes.UpdateNvgValues),
      withLatestFrom(
        this.store.pipe(select(getCropFields)),
        this.store.pipe(select(getSoilTypes)),
        this.store.pipe(select(getCropYear)),
        this.store.pipe(select(getCropFieldType)),
      ),
      mergeMap(
        ([_, cropFields, soilTypes, cropYear, cropFieldType]: [
          UpsertImportedCropFieldsRequest,
          CropField[],
          SoilType[],
          number,
          CropFieldType,
        ]) =>
          from(
            // Convert each cropField into an observable chain executing the checks + upsert API call.
            // This observable chain is then used together with mergeMap + concurrency limit below to avoid
            // overloading the server and map resources with too many requests.
            cropFields.map((cf) =>
              of(cf).pipe(
                // Transform coordinates and set crop field year before validation
                map((cropField) => {
                  const cropFieldWithYear = this.setCropFieldYear(cropField, cropYear, cropYear);
                  return this.transformedCropFields(
                    [cropFieldWithYear],
                    CoordinateSystem.PseudoMercator,
                    CoordinateSystem.WorldGeodetic,
                  )[0];
                }),
                // Validate geometry using backend API
                switchMap((cropField) => {
                  if (!cropField.feature) {
                    return of({ cropField, validation: null });
                  }
                  return this.localCropFieldService
                    .validateGeometry({ feature: cropField.feature })
                    .pipe(map((validation) => ({ cropField, validation })));
                }),
                // Enrich validated CropField data with calculated properties
                map(({ cropField, validation }) => {
                  const cropFieldToUpsert = { ...cropField };

                  if (validation) {
                    cropFieldToUpsert.isNutrientsPollutedArea = validation.isNutrientsPollutedArea;
                  } else {
                    cropFieldToUpsert.isNutrientsPollutedArea = isNutrientsPollutedArea(
                      cropFieldToUpsert.soilTypeId,
                      soilTypes,
                      cropFieldToUpsert.province,
                    );
                  }

                  return cropFieldToUpsert;
                }),
                mergeMap((cropField) => {
                  const cropFieldToUpsert = cropField;

                  cropFieldToUpsert.cropFieldType = cropFieldType;
                  const plantDate =
                    cropFieldToUpsert.plantDate ||
                    cropField.plantDate ||
                    new Date(cropField.startDate);
                  cropFieldToUpsert.plantDate = plantDate;
                  const isPlantDateConfirmed = cropField.isPlantDateConfirmed;
                  cropFieldToUpsert.isPlantDateConfirmed = isPlantDateConfirmed;

                  return this.fieldService
                    .upsertCropField({ ...cropFieldToUpsert, plantDate, isPlantDateConfirmed })
                    .pipe(
                      mergeMap(() => {
                        this.store.dispatch(new UpsertCropFieldSuccess(cropField, true));
                        return [cropField];
                      }),
                    );
                }),
              ),
            ),
          ).pipe(
            mergeMap((obs) => obs, 3), // Limit concurrent requests to 3 (to avoid overloading the server)
            takeLast(1),
            map(() => new UpsertImportedCropFieldsSuccess()),
            startWith(CoreActions.setLoading({ loading: true })),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          ),
      ),
    ),
  );

  private transformedCropFields(
    cropFields: CropField[],
    source: ProjectionLike,
    destination: ProjectionLike,
  ) {
    return cropFields.map((cropField) => ({
      ...cropField,
      feature: cropField.feature
        ? {
            ...cropField.feature,
            cropId: cropField.cropId,
            geometry: {
              ...cropField.feature.geometry,
              coordinates: cropField.feature.geometry.coordinates.map((coordinateArray) =>
                coordinateArray.map((coordinate) => transform(coordinate, source, destination)),
              ),
            },
          }
        : null,
    }));
  }

  private setCropFieldYear(cropField: CropField, year: number, importYear: number) {
    const startDate = new Date(cropField.startDate);
    const endDate = new Date(cropField.endDate);

    if (startDate.getFullYear() === endDate.getFullYear()) {
      startDate.setFullYear(year);
      endDate.setFullYear(year);

      return { ...cropField, startDate: startDate.toISOString(), endDate: endDate.toISOString() };
    }

    // If the end date year is the same as the import year, then the start date is in the previous year.
    const isStartDateInPreviousYear = endDate.getFullYear() === importYear;

    if (!isStartDateInPreviousYear && cropField.externalIdSource === ExternalSource.rvo) {
      startDate.setFullYear(year - 1);
      endDate.setFullYear(year);

      return { ...cropField, startDate: startDate.toISOString(), endDate: endDate.toISOString() };
    }

    // Based on if started date is in the previous year, decide the correct start and end date years.
    const startDateYear = isStartDateInPreviousYear ? year - 1 : year;
    const endDateYear = isStartDateInPreviousYear ? year : year + 1;

    startDate.setFullYear(startDateYear);
    endDate.setFullYear(endDateYear);

    return { ...cropField, startDate: startDate.toISOString(), endDate: endDate.toISOString() };
  }
}
